import React, { useState } from "react";
import { Upload, Eye, Trash2, Link as LinkIcon } from "lucide-react";
import { Button } from "../ui/button";
import { compressImage, compressPDF } from "lib/utils";
import toast from "react-hot-toast";

const UploadResumePortfolio = ({ resume, setResume, portfolio = [], setPortfolio, bgColour, buttonBgColour }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleUpload = async (e, type) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Limit check for portfolio
    if (type === "portfolio" && portfolio.length + files.length > 10) {
      toast.error("You can only upload a maximum of 10 portfolio files.");
      return;
    }

    try {
      setIsProcessing(true);
      const processedFilesData = [];

      for (const file of files) {
        let processedFile = file;

        if (file.type.startsWith('image/')) {
          processedFile = await compressImage(file);
        } else if (file.type === 'application/pdf') {
          processedFile = await compressPDF(file);
        }

        const fileData = {
          id: Math.random().toString(36).substr(2, 9), // unique ID for deletion
          name: processedFile.name,
          size: (processedFile.size / 1024 / 1024).toFixed(1) + " MB",
          file: processedFile,
          url: URL.createObjectURL(processedFile),
        };

        processedFilesData.push(fileData);
      }

      if (type === "resume") {
        setResume(processedFilesData[0]); // Resume stays as a single file
      } else if (type === "portfolio") {
        setPortfolio((prev) => [...prev, ...processedFilesData]);
      }

    } catch (error) {
      console.error("Upload process error:", error);
      toast.error("Some files failed to process.");
    } finally {
      setIsProcessing(false);
      if (e.target) e.target.value = "";
    }
  };

  const removePortfolioItem = (id, url) => {
    if (url) URL.revokeObjectURL(url);
    setPortfolio((prev) => prev.filter((item) => item.id !== id));
  };

  const openFileInNewTab = (url) => {
    const newTab = window.open();
    newTab.document.write(
      `<iframe src="${url}" style="width:100%;height:100%;border:none;"></iframe>`
    );
  };

  return (
    <div className={`w-full border rounded-2xl p-6 ${bgColour ? bgColour : "bg-card"} shadow-sm`}>
      <h2 className="text-base font-bold mb-5">Upload Documents</h2>

      {/* --- RESUME SECTION (Single) --- */}
      <p className="text-sm font-medium mb-2">Resume / CV</p>
      {!resume ? (
        <label className={`flex items-center justify-center gap-3 border border-dashed border-gray-300 rounded-xl p-6 mb-5 ${buttonBgColour ? buttonBgColour : "bg-button-generic/80 hover:bg-button-generic/80"} ${isProcessing ? "opacity-50 cursor-not-allowed" : `cursor-pointer`} transition`}>
          <Upload size={16} />
          <span className="text-sm">Upload Resume/CV</span>
          <input
            type="file"
            accept="image/png,image/jpeg,application/pdf"
            className="hidden"
            onChange={(e) => handleUpload(e, "resume")}
            disabled={isProcessing}
          />
        </label>
      ) : (
        <div className="flex justify-between items-center p-4 border rounded-xl mb-5 bg-white shadow-sm">
          <div className="flex items-center gap-2 text-sm w-2/3">
            <LinkIcon size={16} />
            <span className="font-semibold truncate">{resume.name}</span>
            <span className="text-gray-500">{resume.size}</span>
          </div>
          <div className="flex gap-2">
            <Button className="border border-grey-3 rounded-lg p-2" onClick={() => openFileInNewTab(resume.url)}>
              <Eye size={16} />
            </Button>
            <Button className="border border-grey-3 rounded-lg p-2" onClick={() => { URL.revokeObjectURL(resume.url); setResume(null); }}>
              <Trash2 size={16} />
            </Button>
          </div>
        </div>
      )}

      {/* --- PORTFOLIO SECTION (Multiple) --- */}
      <div className="flex justify-between items-center mb-2">
        <p className="text-sm font-medium">Portfolio (Up to 10)</p>
        <span className="text-xs text-gray-500">{portfolio.length} / 10</span>
      </div>

      {/* List of uploaded portfolio items */}
      <div className="space-y-3 mb-4">
        {portfolio.map((item) => (
          <div key={item.id} className="flex justify-between items-center p-4 border rounded-xl bg-white shadow-sm">
            <div className="flex items-center gap-2 text-sm w-2/3">
              <LinkIcon size={16} />
              <span className="font-semibold truncate">{item.name}</span>
              <span className="text-gray-500">{item.size}</span>
            </div>
            <div className="flex gap-2">
              <Button className="border border-grey-3 rounded-lg p-2" onClick={() => openFileInNewTab(item.url)}>
                <Eye size={16} />
              </Button>
              <Button className="border border-grey-3 rounded-lg p-2" onClick={() => removePortfolioItem(item.id, item.url)}>
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Show upload button only if under limit */}
      {portfolio.length < 10 && (
        <label className={`flex items-center justify-center gap-3 border border-dashed border-gray-300 rounded-xl p-6 ${buttonBgColour ? buttonBgColour : "bg-button-generic/80 hover:bg-button-generic/80"} ${isProcessing ? "opacity-50 cursor-not-allowed" : `cursor-pointer`} transition`}>
          <Upload size={16} />
          <span className="text-sm">Upload Portfolio {portfolio.length > 0 ? "More" : ""}</span>
          <input
            type="file"
            multiple // Allows selecting multiple files at once
            className="hidden"
            accept="image/png,image/jpeg,application/pdf"
            onChange={(e) => handleUpload(e, "portfolio")}
            disabled={isProcessing}
          />
        </label>
      )}
    </div>
  );
};

export default UploadResumePortfolio;
